import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const api = axios.create({
  baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('gty_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401 — token expired/invalid → clear and redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('gty_token');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  getDiscordAuthUrl: () => api.get('/auth/discord'),
  handleCallback: (code) => api.post('/auth/callback', { code }),
  getUserProfile: () => api.get('/user/profile'),
  getPaymentStatus: () => api.get('/user/payment-status'),
};

// Applications API (public — requires login)
export const applyAPI = {
  check: () => api.get('/apply/check'),
  submit: (data) => api.post('/apply', data),
};

// Management Applications API
export const applicationsAPI = {
  list: () => api.get('/management/applications'),
  stats: () => api.get('/management/applications/stats'),
  get: (id) => api.get(`/management/applications/${id}`),
  setStatus: (id, status) => api.patch(`/management/applications/${id}/status`, { status }),
  addNote: (id, content) => api.post(`/management/applications/${id}/notes`, { content }),
  listBlocked: () => api.get('/management/blocked'),
  block: (data) => api.post('/management/blocked', data),
  unblock: (id) => api.delete(`/management/blocked/${id}`),
  triggerOnboarding: (discordId) => api.post(`/management/onboarding/${discordId}`),
};

// Management API
export const managementAPI = {
  getDriversStatus: () => api.get('/management/drivers'),
  getDriversList: () => api.get('/management/drivers/list'),
  updateDriverProfile: (driverId, data) => api.patch(`/management/drivers/${driverId}/profile`, data),
  getRepairReports: () => api.get('/management/documents/repairs'),
  updateRepairReport: (id, data) => api.patch(`/management/documents/repairs/${id}`, data),
  deleteRepairReport: (id) => api.delete(`/management/documents/repairs/${id}`),
  getMissingItemsReports: () => api.get('/management/documents/missing-items'),
  updateMissingItemsReport: (id, data) => api.patch(`/management/documents/missing-items/${id}`, data),
  deleteMissingItemsReport: (id) => api.delete(`/management/documents/missing-items/${id}`),
};

// Company Documents API
export const companyDocsAPI = {
  getDocuments: () => api.get('/documents'),
  createDocument: (data) => api.post('/management/documents', data),
  updateDocument: (id, data) => api.patch(`/management/documents/${id}`, data),
  deleteDocument: (id) => api.delete(`/management/documents/${id}`),
};

// Profile API
export const profileAPI = {
  updateProfile: (data) => api.patch('/user/profile', data),
  uploadAvatar: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/user/avatar', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Gallery API
// Note: filename column now stores full Supabase URLs for new uploads.
// getImageUrl kept for backwards-compat with any old /uploads/ filenames still in DB.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const publicApi = axios.create({ baseURL: API_URL }); // no auth header
export const galleryAPI = {
  getImages: () => publicApi.get('/gallery'),
  uploadImage: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/gallery/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteImage: (id) => api.delete(`/gallery/${id}`),
  // Returns the URL to display: if already a full URL (Supabase), use as-is; else prefix with backend
  getImageUrl: (filename) => filename && filename.startsWith('http') ? filename : `${BACKEND_URL}/uploads/${filename}`,
};

// Rental API
export const rentalAPI = {
  submitRental: (data) => api.post('/rentals', data),
  getRentalStatus: () => api.get('/rentals/status'),
  endRental: (truckId) => api.post(`/rentals/${truckId}/end`),
  getCustomTrucks: () => api.get('/rental-trucks/custom'),
  getRemovedTrucks: () => api.get('/rental-trucks/removed'),
  addCustomTruck: (data) => api.post('/rental-trucks/custom', data),
  deleteCustomTruck: (dbId) => api.delete(`/rental-trucks/custom/${dbId}`),
  deleteTruck: (truckId) => api.delete(`/rental-trucks/${truckId}`),
};

// News API
export const newsAPI = {
  getNews: () => api.get('/news'),
  createNews: (data) => api.post('/news', data),
  deleteNews: (id) => api.delete(`/news/${id}`),
};

// Trucks API
export const trucksAPI = {
  getTrucks: () => api.get('/trucks'),
  getTruckStats: () => api.get('/trucks/stats'),
  useTruck: (truckId) => api.post(`/trucks/${truckId}/use`),
  returnTruck: (truckId) => api.post(`/trucks/${truckId}/return`),
  overrideReturn: (truckId) => api.post(`/trucks/${truckId}/force-return`),
  getTruckLogs: () => api.get('/trucks/logs'),
  reportRepair: (truckId, data) => api.post(`/trucks/${truckId}/repair`, data),
  reportMissingItems: (truckId, data) => api.post(`/trucks/${truckId}/missing-items`, data),
};

// Payment Submissions API
export const paymentSubmissionsAPI = {
  submit: (formData) => api.post('/payments/submit', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  myHistory: () => api.get('/payments/my'),
  // management
  list: () => api.get('/management/payments'),
  stats: () => api.get('/management/payments/stats'),
  markRead: (id) => api.patch(`/management/payments/${id}/read`),
  confirm: (id) => api.patch(`/management/payments/${id}/confirm`),
  reject: (id, reason) => api.patch(`/management/payments/${id}/reject`, { reason }),
  bulkConfirm: (ids) => api.post('/management/payments/bulk-confirm', { ids }),
};

// Broadcast API (legacy Discord channels + driver direct messages)
export const broadcastAPI = {
  getChannels:  (role) => api.get('/management/broadcast/channels', { params: role && role !== 'all' ? { role } : {} }),
  send:         (channel_ids, message) => api.post('/management/broadcast', { channel_ids, message }),
  getDrivers:   () => api.get('/management/guild-members'),
  sendToDrivers: (data) => api.post('/management/broadcast/send', data),
};


// User Preferences API
export const preferencesAPI = {
  get: () => api.get('/user/preferences'),
  update: (data) => api.patch('/user/preferences', data),
};

// Mechanic API
export const mechanicAPI = {
  getIncidents: () => api.get('/mechanic/incidents'),
  startRepair: (id) => api.patch(`/mechanic/incidents/${id}/start`),
  finishRepair: (id) => api.patch(`/mechanic/incidents/${id}/finish`),
};

// Channel Setup API
export const channelSetupAPI = {
  getMembers: () => api.get('/management/guild-members'),
  create: (data) => api.post('/management/create-channel', data),
};

// Staff Notes API
export const staffNotesAPI = {
  get: () => api.get('/staff/notes'),
  save: (content, images) => api.patch('/staff/notes', { content, images }),
  uploadImage: (formData) => api.post('/staff/notes/images', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Interview Chat API
export const interviewAPI = {
  getMessages: (id) => api.get(`/management/applications/${id}/messages`),
  sendMessage: (id, content) => api.post(`/management/applications/${id}/message`, { content }),
};

// Team API
export const teamAPI = {
  getTeam:      ()         => api.get('/team'),
  updateMember: (id, data) => api.put(`/management/team/${id}`, data),
  addMember:    (data)     => api.post('/management/team', data),
  deleteMember: (id)       => api.delete(`/management/team/${id}`),
  uploadPhoto:  (id, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/team/${id}/photo`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Inventory API
export const inventoryAPI = {
  // Categories
  listCategories:   ()                        => api.get('/inventory/categories'),
  createCategory:   (data)                    => api.post('/inventory/categories', data),
  updateCategory:   (id, data)                => api.patch(`/inventory/categories/${id}`, data),
  deleteCategory:   (id, moveTo)              => api.delete(`/inventory/categories/${id}`, { params: moveTo ? { move_to: moveTo } : {} }),
  // Items / warehouses (kind discriminator)
  listItems:        (kind)                    => api.get('/inventory/items', { params: kind ? { kind } : {} }),
  createItem:       (data)                    => api.post('/inventory/items', data),
  updateItem:       (id, data)                => api.patch(`/inventory/items/${id}`, data),
  deleteItem:       (id)                      => api.delete(`/inventory/items/${id}`),
  adjust:           (id, delta, reason = '')  => api.post(`/inventory/items/${id}/adjust`, { delta, reason }),
  reorder:          (kind, order)             => api.post('/inventory/items/reorder', { kind, order }),
  bulk:             (data)                    => api.post('/inventory/items/bulk', data),
  // Logs (management)
  listLogs:         (itemId)                  => api.get('/inventory/logs', { params: itemId ? { item_id: itemId } : {} }),
  // Image upload
  uploadImage:      (file)                    => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/inventory/upload-image', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// CDL Scheduling API
export const cdlAPI = {
  submit:    (data) => api.post('/cdl/submit', data),
  my:        ()     => api.get('/cdl/my'),
  list:      ()     => api.get('/management/cdl'),
  setStatus: (id, status, management_note) => api.patch(`/management/cdl/${id}/status`, { status, management_note }),
};

// LOA (Leave of Absence) API
export const loaAPI = {
  submit: (formData) => api.post('/loa/submit', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  myRequests: () => api.get('/loa/my'),
  list: () => api.get('/management/loa'),
  approve: (id) => api.patch(`/management/loa/${id}/approve`),
  deny: (id, reason) => api.patch(`/management/loa/${id}/deny`, { reason }),
  markReturned: (id) => api.patch(`/management/loa/${id}/return`),
};

// Tutorials API
export const tutorialsAPI = {
  getAll: () => api.get('/tutorials'),
  create: (data) => api.post('/management/tutorials', data),
  update: (id, data) => api.patch(`/management/tutorials/${id}`, data),
  delete: (id) => api.delete(`/management/tutorials/${id}`),
  reorder: (order) => api.post('/management/tutorials/reorder', { order }),
  uploadImage: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/management/tutorials/images', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};
